/**
 * Instrument definitions for default avatars.
 * Each entry maps to an SVG at: default_profile_images/instruments/<key>.svg
 * dominantHues are HSL hues extracted from the Flat SVG fill colors.
 */
export const INSTRUMENTS = [
  { key: "accordion", displayName: "Accordion", dominantHues: [40, 330] },
  { key: "banjo", displayName: "Banjo", dominantHues: [48, 15] },
  { key: "bell", displayName: "Bell", dominantHues: [30, 42] },
  { key: "control-knobs", displayName: "Knobs", dominantHues: [0, 207] },
  { key: "drum", displayName: "Drum", dominantHues: [340, 40] },
  { key: "flute", displayName: "Flute", dominantHues: [40, 255] },
  { key: "guitar", displayName: "Guitar", dominantHues: [0, 260] },
  { key: "headphone", displayName: "Headphone", dominantHues: [210, 275] },
  { key: "level-slider", displayName: "Slider", dominantHues: [210, 120] },
  { key: "long-drum", displayName: "Drum", dominantHues: [40, 45] },
  { key: "maracas", displayName: "Maracas", dominantHues: [15, 5] },
  { key: "megaphone", displayName: "Megaphone", dominantHues: [40, 45] },
  { key: "microphone", displayName: "Microphone", dominantHues: [340, 0] },
  {
    key: "musical-keyboard",
    displayName: "Keyboard",
    dominantHues: [0, 200],
  },
  { key: "musical-note", displayName: "Note", dominantHues: [40, 255] },
  { key: "musical-notes", displayName: "Notes", dominantHues: [210, 120] },
  { key: "musical-score", displayName: "Score", dominantHues: [270, 280] },
  { key: "postal-horn", displayName: "Horn", dominantHues: [40, 45] },
  { key: "radio", displayName: "Radio", dominantHues: [275, 0] },
  { key: "saxophone", displayName: "Saxophone", dominantHues: [210, 40] },
  {
    key: "speaker-high-volume",
    displayName: "Speaker",
    dominantHues: [275, 210],
  },
  {
    key: "studio-microphone",
    displayName: "Microphone",
    dominantHues: [340, 0],
  },
  { key: "trumpet", displayName: "Trumpet", dominantHues: [45, 0] },
  { key: "violin", displayName: "Violin", dominantHues: [15, 310] },
] as const;

export type InstrumentKey = (typeof INSTRUMENTS)[number]["key"];

/**
 * Color pairs for split backgrounds. Each pair is [leftColor, rightColor].
 * Curated for visual appeal on dark UI.
 */
export const COLOR_PAIRS: readonly [string, string][] = [
  ["#FF6B6B", "#4ECDC4"],
  ["#845EC2", "#FFC75F"],
  ["#FF9671", "#00C9A7"],
  ["#F9F871", "#FF6F91"],
  ["#C34A36", "#008B74"],
  ["#5B5EA6", "#9B89B3"],
  ["#00B4D8", "#E76F51"],
  ["#2A9D8F", "#E9C46A"],
  ["#6A0572", "#AB83A1"],
  ["#264653", "#E76F51"],
  ["#F4A261", "#2A9D8F"],
  ["#E63946", "#457B9D"],
  ["#A8DADC", "#1D3557"],
  ["#FF006E", "#3A86FF"],
  ["#8338EC", "#FF006E"],
  ["#06D6A0", "#118AB2"],
  ["#EF476F", "#FFD166"],
  ["#073B4C", "#06D6A0"],
  ["#F72585", "#7209B7"],
  ["#4361EE", "#4CC9F0"],
  ["#FB8500", "#023047"],
  ["#FFBE0B", "#FB5607"],
  ["#3A0CA3", "#F72585"],
  ["#480CA8", "#4CC9F0"],
  ["#560BAD", "#B5179E"],
  ["#7400B8", "#80FFDB"],
  ["#6930C3", "#64DFDF"],
  ["#5390D9", "#48BFE3"],
  ["#FF4D6D", "#C9184A"],
  ["#2EC4B6", "#E71D36"],
  ["#FF7F11", "#FF1654"],
  ["#247BA0", "#70C1B3"],
  ["#50514F", "#F25F5C"],
  ["#CB997E", "#6B705C"],
  ["#B56576", "#355070"],
  ["#E56B6F", "#EAAC8B"],
];

/**
 * Returns the HSL hue (0-360) from a hex color.
 */
export function hexToHue(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return 0;
  let h = 0;
  if (max === r) h = ((g - b) / d + 6) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return Math.round(h * 60);
}

const MIN_HUE_DISTANCE = 30;

function hueDistance(h1: number, h2: number): number {
  const d = Math.abs(h1 - h2);
  return Math.min(d, 360 - d);
}

/**
 * Filter color pairs compatible with an instrument's dominant hues.
 * Both colors must have sufficient hue distance from all dominant hues.
 */
export function getCompatibleColorPairs(
  dominantHues: readonly number[],
): [string, string][] {
  return COLOR_PAIRS.filter(([left, right]) => {
    const leftHue = hexToHue(left);
    const rightHue = hexToHue(right);
    return dominantHues.every(
      (dh) =>
        hueDistance(leftHue, dh) >= MIN_HUE_DISTANCE &&
        hueDistance(rightHue, dh) >= MIN_HUE_DISTANCE,
    );
  });
}

/**
 * Get the CDN URL for an instrument SVG.
 */
export function getInstrumentImageUrl(
  cdnBase: string,
  instrumentKey: string,
): string {
  return `${cdnBase}/images/_default_profile_images/instruments/${instrumentKey}.svg`;
}
